'use client';

import React from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { useLocalizedNavigation } from '@/contexts/i18n/navigation';
import '@/components/logo/logo.css';

interface LogoProps {
    /** 附加样式类名 */
    className?: string;
    /** Logo 缩放尺寸 */
    size?: 'sm' | 'md' | 'lg';
    /** 是否允许点击跳转 */
    clickable?: boolean;
    /** 自定义点击回调 */
    onClick?: () => void;
    /** 品牌首页按钮的可访问性标签 */
    ariaLabel?: string;
}

interface EnterDoorLogoMarkProps {
    /** 附加样式类名 */
    className?: string;
    /** 测试标识；不传时使用 Header Logo 的默认标识 */
    testId?: string;
}

const LOGO_SCALE_CLASS = {
    sm: 'scale-[0.875]',
    md: 'scale-100',
    lg: 'scale-[1.125]',
} as const;

/**
 * Workspace 当前使用的 Enter 字标，颜色跟随页面 foreground token。
 */
const EnterWordmark: React.FC = () => (
    <svg
        aria-hidden="true"
        className="block h-[13px] w-auto -translate-y-[0.2px]"
        data-testid="enter-home-wordmark"
        fill="none"
        viewBox="0 0 238 78"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M127.865 35.501V58.2029C127.865 60.0084 128.298 61.314 129.166 62.12C130.033 62.926 131.494 63.329 133.551 63.329H140.179V76.4368H131.334C119.961 76.4368 113.148 70.8917 113.148 59.8015V7.88867H127.865V20.8496H140.179V35.501H127.865Z"
            fill="currentColor"
        />
        <path
            d="M200.435 48.1679C200.435 50.1167 200.305 51.8703 200.047 53.4291H160.724C161.048 57.3261 162.408 60.3789 164.802 62.5873C167.196 64.7958 170.142 65.8997 173.637 65.8997C178.686 65.8997 182.279 63.7239 184.415 59.3721H199.076C197.523 64.5682 194.546 68.8388 190.143 72.1839C185.741 75.529 180.337 77.2014 173.929 77.2014C168.75 77.2014 164.107 76.0486 159.996 73.7427C155.885 71.437 152.681 68.1732 150.383 63.9512C148.086 59.7294 146.937 54.858 146.937 49.3372C146.937 43.7512 148.069 38.8474 150.335 34.6254C152.601 30.4036 155.772 27.156 159.85 24.8828C163.928 22.6093 168.622 21.4727 173.929 21.4727C179.042 21.4727 183.623 22.5769 187.668 24.7854C191.712 26.9938 194.852 30.1275 197.086 34.1871C199.319 38.2466 200.435 42.907 200.435 48.1679ZM186.357 44.2708C186.293 40.7636 185.03 37.9543 182.57 35.8433C180.11 33.7325 177.1 32.6769 173.54 32.6769C170.175 32.6769 167.342 33.6998 165.045 35.7458C162.747 37.7921 161.339 40.6334 160.821 44.2708H186.357Z"
            fill="currentColor"
        />
        <path
            d="M48.9492 14.6924H14.6396V30.9932H41.6729V45.6445H14.6396V61.5938H48.9492V76.2861H0V0H48.9492V14.6924Z"
            fill="currentColor"
        />
        <path
            clipRule="evenodd"
            d="M221.835 37.2391V76.2863H207.193V34.6789C207.193 31.4762 208.46 28.4049 210.718 26.1403C212.975 23.8758 216.036 22.6035 219.228 22.6035H237.04V37.2391H221.835Z"
            fill="currentColor"
            fillRule="evenodd"
        />
        <path
            d="M85.1762 22.0391C91.5116 22.0391 96.6317 24.054 100.535 28.0839C104.438 32.1138 106.39 37.7396 106.39 44.9609V76.2869H92.9515V46.7985C92.9515 42.543 91.8956 39.2708 89.7838 36.9818C87.672 34.6927 84.7922 33.5484 81.1446 33.5484C77.4336 33.5484 74.5059 34.6927 72.3614 36.9818C70.217 39.2708 69.1457 42.543 69.1457 46.7985V76.2869H55.707L55.707 22.8129H69.1457V29.4863C70.9369 27.1651 73.2253 25.3436 76.0091 24.0218C78.7928 22.7001 81.8492 22.0391 85.1762 22.0391Z"
            fill="currentColor"
        />
    </svg>
);

/**
 * Enter 品牌门形标识，不包含跳转或按钮语义，可复用于 Header 与 Footer。
 */
export const EnterDoorLogoMark: React.FC<EnterDoorLogoMarkProps> = ({
    className,
    testId = 'enter-home-door-logo',
}) => (
    <span
        aria-hidden="true"
        className={cn(
            'relative flex size-4 shrink-0 items-center justify-center',
            className
        )}
        data-testid={testId}
    >
        <span className="enter-home-door-logo-shape relative block h-4 w-[13.6px] shrink-0 overflow-hidden">
            <span className="enter-home-door-logo-layer enter-home-door-logo-layer-hover" />
        </span>
    </span>
);

/**
 * Enter Home 品牌 Logo，视觉与 Workspace 的门形标识和字标保持一致。
 */
export const Logo: React.FC<LogoProps> = ({
    className,
    size = 'md',
    clickable = true,
    onClick,
    ariaLabel = 'Enter home',
}) => {
    const localizedNavigation = useLocalizedNavigation();

    const handleClick = () => {
        if (!clickable) return;

        if (onClick) {
            onClick();
            return;
        }

        localizedNavigation.push('/');
    };

    return (
        <button
            type="button"
            aria-label={ariaLabel}
            className={cn(
                'enter-home-brand-logo inline-flex h-8 origin-left items-center border-0 bg-transparent p-0 text-foreground',
                LOGO_SCALE_CLASS[size],
                clickable ? 'cursor-pointer' : 'cursor-default',
                className
            )}
            data-testid="enter-home-brand-logo"
            disabled={!clickable}
            onClick={handleClick}
        >
            <span className="flex size-8 items-center justify-center px-2">
                <EnterDoorLogoMark />
            </span>
            <span className="-ml-1 flex h-4 items-end">
                <EnterWordmark />
            </span>
        </button>
    );
};
